import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { X, Pencil, Eraser, Undo2, Save } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PEN_COLOR = '#ef4444';
const PEN_WIDTH = 4;

// Higher z-index than ModalOverlay (10000) so it sits above the Bug modal.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 16px;
`;

const Shell = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  width: 100%;
  max-width: 1100px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ToolBar = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 10px 18px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary};
`;

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.$active ? PEN_COLOR : props.theme.colors.border};
  background: ${props => props.$active ? PEN_COLOR + '15' : 'transparent'};
  color: ${props => props.$active ? PEN_COLOR : props.theme.colors.text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${props => props.$active ? PEN_COLOR + '25' : props.theme.colors.backgroundSecondary};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Spacer = styled.div`
  flex: 1;
`;

const CanvasWrap = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: repeating-conic-gradient(${props => props.theme.colors.backgroundSecondary} 0% 25%, ${props => props.theme.colors.background} 0% 50%) 50% / 24px 24px;
`;

const StyledCanvas = styled.canvas`
  display: block;
  max-width: 100%;
  max-height: 100%;
  cursor: crosshair;
  background: #fff;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
  touch-action: none;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FootHint = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
`;

const BtnPrimary = styled.button`
  padding: 9px 16px;
  background: ${PEN_COLOR};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.05); }
`;

const BtnSecondary = styled.button`
  padding: 9px 16px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${props => props.theme.colors.backgroundSecondary}; }
`;

/**
 * Image annotator. Loads the source image (from a blob URL) onto a canvas,
 * lets the user draw red strokes on top, and emits the merged image as a
 * Blob via onSave. Strokes are kept as a stack so Undo can revert step by
 * step. Save flattens everything to a single PNG.
 */
export const ImageAnnotator = ({ isOpen, imageUrl, fileName, onSave, onCancel }) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const currentStrokeRef = useRef([]); // {x,y}[]
  const strokesRef = useRef([]); // ({x,y}[])[]
  const [, forceRender] = useState(0);
  const [saving, setSaving] = useState(false);

  // Load image on open and size the canvas.
  useEffect(() => {
    if (!isOpen || !imageUrl) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      imgRef.current = img;
      strokesRef.current = [];
      const cv = canvasRef.current;
      if (!cv) return;
      cv.width = img.naturalWidth;
      cv.height = img.naturalHeight;
      redraw();
    };
    img.onerror = () => console.warn('ImageAnnotator: failed to load source image');
    img.src = imageUrl;
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, imageUrl]);

  // Redraw the entire canvas: image + every saved stroke + the current in-progress stroke.
  const redraw = useCallback(() => {
    const cv = canvasRef.current;
    const img = imgRef.current;
    if (!cv || !img) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0);

    ctx.strokeStyle = PEN_COLOR;
    ctx.lineWidth = PEN_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const drawStroke = (pts) => {
      if (pts.length < 2) {
        if (pts.length === 1) {
          ctx.beginPath();
          ctx.arc(pts[0].x, pts[0].y, PEN_WIDTH / 2, 0, Math.PI * 2);
          ctx.fillStyle = PEN_COLOR;
          ctx.fill();
        }
        return;
      }
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    };

    for (const stroke of strokesRef.current) drawStroke(stroke);
    if (currentStrokeRef.current.length > 0) drawStroke(currentStrokeRef.current);
  }, []);

  // Convert a pointer event to canvas coordinates (account for CSS scaling).
  const pointFromEvent = (e) => {
    const cv = canvasRef.current;
    if (!cv) return null;
    const rect = cv.getBoundingClientRect();
    const scaleX = cv.width / rect.width;
    const scaleY = cv.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const p = pointFromEvent(e);
    if (!p) return;
    lastPointRef.current = p;
    currentStrokeRef.current = [p];
    redraw();
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const p = pointFromEvent(e);
    if (!p) return;
    currentStrokeRef.current.push(p);
    lastPointRef.current = p;
    redraw();
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentStrokeRef.current.length > 0) {
      strokesRef.current.push(currentStrokeRef.current);
      currentStrokeRef.current = [];
      forceRender(n => n + 1); // re-render to enable Undo/Save
    }
  };

  const handleUndo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    redraw();
    forceRender(n => n + 1);
  };

  const handleClear = () => {
    if (strokesRef.current.length === 0) return;
    if (!window.confirm('Clear all annotations?')) return;
    strokesRef.current = [];
    redraw();
    forceRender(n => n + 1);
  };

  const handleSave = async () => {
    const cv = canvasRef.current;
    if (!cv) return;
    setSaving(true);
    try {
      const blob = await new Promise(res => cv.toBlob(res, 'image/png', 0.95));
      if (!blob) throw new Error('Canvas export failed');
      await onSave(blob);
    } catch (err) {
      console.error('ImageAnnotator: save failed', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const hasStrokes = strokesRef.current.length > 0;

  return (
    <Backdrop data-bug-reporter-ignore="true" onClick={(e) => e.stopPropagation()}>
      <Shell theme={theme}>
        <Header theme={theme}>
          <HeaderTitle theme={theme}>
            <Pencil size={16} color={PEN_COLOR} />
            Annotate: {fileName}
          </HeaderTitle>
          <BtnSecondary theme={theme} onClick={onCancel} disabled={saving} aria-label="Close">
            <X size={16} />
          </BtnSecondary>
        </Header>

        <ToolBar theme={theme}>
          <ToolButton theme={theme} $active>
            <Pencil size={14} /> Red pen
          </ToolButton>
          <ToolButton theme={theme} onClick={handleUndo} disabled={!hasStrokes || saving}>
            <Undo2 size={14} /> Undo
          </ToolButton>
          <ToolButton theme={theme} onClick={handleClear} disabled={!hasStrokes || saving}>
            <Eraser size={14} /> Clear
          </ToolButton>
          <Spacer />
          <FootHint theme={theme}>
            {hasStrokes ? `${strokesRef.current.length} stroke${strokesRef.current.length === 1 ? '' : 's'}` : 'Drag to draw'}
          </FootHint>
        </ToolBar>

        <CanvasWrap theme={theme}>
          <StyledCanvas
            ref={canvasRef}
            theme={theme}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={handlePointerUp}
          />
        </CanvasWrap>

        <Footer theme={theme}>
          <FootHint theme={theme}>
            Annotations are saved into the image and uploaded back to the bug report.
          </FootHint>
          <div style={{ display: 'flex', gap: 8 }}>
            <BtnSecondary theme={theme} onClick={onCancel} disabled={saving}>
              Cancel
            </BtnSecondary>
            <BtnPrimary theme={theme} onClick={handleSave} disabled={saving}>
              <Save size={14} />
              {saving ? 'Saving…' : 'Save annotation'}
            </BtnPrimary>
          </div>
        </Footer>
      </Shell>
    </Backdrop>
  );
};

export default ImageAnnotator;
